'use client';

import { useState, useCallback } from 'react';
import { emptySet } from '@federa/const/empty-set';
import { isBrowser } from '@federa/utils/browser/detection';
import { useEventListener } from './useEventListener.ts';
import { useFlagSetter } from './useFlagSetter.ts';

// Defined here rather than in shared.ts to avoid a circular dependency.

export function useVisibilityChangeListener(handler: () => void): void {
	const hasBrowser = isBrowser();
	useEventListener('visibilitychange', handler, {
		target: hasBrowser ? document : null,
		enabled: hasBrowser,
	});
}

function useFocusBlurListeners(onFocus: () => void, onBlur: () => void): void {
	const hasBrowser = isBrowser();
	// Bind to `window` only where one exists, and stay disabled otherwise -- one decision, shared by both listeners.
	// A fresh record per render either way, so sharing it changes no identity `useEventListener` observes.
	const windowListenerOptions = { target: hasBrowser ? window : null, enabled: hasBrowser };
	useEventListener('focus', onFocus, windowListenerOptions);
	useEventListener('blur', onBlur, windowListenerOptions);
}

/** Detects the page becoming visible again after being backgrounded -- load-bearing on mobile (especially iframed), where WebSocket connections drop while backgrounded and must be reconnected on resume. */
export function usePageVisibility() {
	const [isVisible, setIsVisible] = useState(() => {
		if (!isBrowser()) return true;
		return document.visibilityState === 'visible';
	});
	const [callbacks] = useState<Set<(visible: boolean) => void>>(emptySet);

	const onVisibilityChange = useCallback(
		(callback: (visible: boolean) => void) => {
			callbacks.add(callback);
			return () => {
				void callbacks.delete(callback);
			};
		},
		[callbacks],
	);

	const notify = useCallback(
		(visible: boolean) => {
			setIsVisible(visible);
			for (const cb of callbacks) cb(visible);
		},
		[callbacks],
	);

	const handleVisibilityChange = useCallback(() => {
		if (!isBrowser()) return;
		notify(document.visibilityState === 'visible');
	}, [notify]);

	const handleFocus = useFlagSetter(notify, true);
	const handleBlur = useFlagSetter(notify, false);

	useVisibilityChangeListener(handleVisibilityChange);
	// Focus/blur is the secondary signal, for older browsers and edge cases.
	useFocusBlurListeners(handleFocus, handleBlur);

	return { isVisible, onVisibilityChange };
}
